import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockBillingAffordancesEnabled } from '../../../shared/commercialActions';

export type HostedRedirectTarget = { kind: 'external'; url: string } | { kind: 'internal'; path: string };

/**
 * Decides where a server-supplied "go to the secure payment page" URL may send the browser.
 *  - a real provider URL must be absolute `https:` (Stripe Checkout, Stripe customer portal);
 *  - a same-app relative path (`/settings/...`) is accepted ONLY when mock billing affordances are on, because only
 *    the mock sandbox uses one, and it must stay inside the SPA to keep its in-memory session.
 * Anything else (a `javascript:` or `data:` URL, a protocol-relative `//host`, plain `http:`) is refused.
 */
export function resolveHostedRedirect(url: string, allowInternal: boolean): HostedRedirectTarget | null {
  if (allowInternal && url.startsWith('/') && !url.startsWith('//')) {
    return { kind: 'internal', path: url };
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? { kind: 'external', url: parsed.toString() } : null;
  } catch {
    return null;
  }
}

/** Thin wrapper so tests can replace the one place the browser actually leaves the app. */
export const hostedRedirectBrowser = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

export const HOSTED_REDIRECT_ERROR = 'We could not open the secure payment page. Please try again, or contact billing support.';

/** Returns a function that follows a hosted-page URL, or throws `HOSTED_REDIRECT_ERROR` when it is not a safe one. */
export function useHostedRedirect(): (url: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (url: string) => {
      const target = resolveHostedRedirect(url, mockBillingAffordancesEnabled);
      if (!target) throw new Error(HOSTED_REDIRECT_ERROR);
      if (target.kind === 'internal') navigate(target.path);
      else hostedRedirectBrowser.assign(target.url);
    },
    [navigate],
  );
}

/** Opens a provider-hosted invoice/receipt link only if it is a plain https URL. */
export function safeExternalUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}
